import { Button, Select, Tooltip } from 'antd'
import { SelectProps } from 'antd/lib'
import classNames from 'classnames'
import IconDown from 'components/icons/IconDown'
import IconTooltip from 'components/icons/IconTooltip'
import { ReactNode } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'
import { cn, orderListByKey } from 'utils/common'
import { infoNodeComponents, TInfoNodeCb } from '../helpers/InfoNode'
import FormMessage from '../helpers/FormMessage'
import { ISelectOption } from 'models/common'

interface IProps<T extends FieldValues>
  extends Pick<SelectProps, 'suffixIcon' | 'className' | 'defaultValue' | 'onChange' | 'value' | 'disabled'> {
  label?: string
  control?: Control<T>
  placeholder?: string
  name: Path<T>
  options?: ISelectOption[]
  isSort?: boolean
  infoNode?: ReactNode | TInfoNodeCb
}

export default function BaseSelect<T extends FieldValues = FieldValues>(props: IProps<T>) {
  const { options, label, placeholder, className, infoNode, isSort = true, ...rest } = props

  const filterOption = (input: string, option?: any) => {
    return (
      (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) ||
      (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
    )
  }

  return (
    <>
      {props.control ? (
        <Controller
          control={props.control}
          name={props.name}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div className='relative'>
              {label && (
                <label
                  className={cn('text-sm text-neutral-700 font-medium flex items-center', {
                    'text-disabled': rest.disabled
                  })}
                >
                  {label}
                  {infoNode && (
                    <Tooltip
                      title={typeof infoNode === 'function' ? infoNode(infoNodeComponents) : infoNode}
                      className='text-xs'
                    >
                      <Button
                        className='w-4 min-w-4 h-4 min-h-4 border-none ml-1'
                        shape='circle'
                        icon={<IconTooltip />}
                      />
                    </Tooltip>
                  )}
                </label>
              )}
              <Select
                className={cn(
                  'w-full h-10 min-h-10 [&>*]:pl-2   [&>*]:text-base [&>*]:font-semibold [&>*]:text-neutral-700 [&>div]:bg-neutral-600 ',
                  { 'mt-1': label },
                  {
                    '[&>*]:text-disabled': rest.disabled
                  },
                  className,
                  {
                    '[&>*]:border-b-error': error
                  }
                )}
                onChange={onChange}
                listHeight={200}
                status={error ? 'error' : ''}
                value={value}
                showSearch={true}
                suffixIcon={<IconDown />}
                placeholder={placeholder}
                maxTagCount={'responsive'}
                filterOption={filterOption}
                {...rest}
              >
                {options &&
                  orderListByKey(options, 'label', 'asc', isSort)?.map((option) => (
                    <Select.Option key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </Select.Option>
                  ))}
              </Select>
              <FormMessage error={error} />
            </div>
          )}
        />
      ) : (
        <Select
          suffixIcon={<IconDown />}
          showSearch={true}
          listHeight={200}
          className={classNames(
            'h-10 [&>*]:text-base [&>*]:font-semibold [&>*]:text-neutral-700 [&>div]:bg-neutral-600 [&>div]:mr-3 [&>*]:border-0',
            className
          )}
          filterOption={filterOption}
          {...rest}
        >
          {options &&
            orderListByKey(options, 'label', 'asc', isSort)?.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
        </Select>
      )}
    </>
  )
}
